<template>
  <ContentPanel>
    <ContentWrapper centered-content large-padding class="content-block">
      <IconContainer type="success">
        <FhIcon
          size="md"
          color="success-default"
          :icon="check"
          data-test-id="success-icon"
        />
      </IconContainer>

      <FhText tag="h1" variant="display-xl" data-test-id="waiver-signed">
        {{ T('Waiver signed!') }}
      </FhText>

      <FhText
        v-if="!props.isNotExisted"
        variant="body-md"
        data-test-id="confirmation-email-text"
        class="text-center"
      >
        {{ computedConfirmationEmailText }}
      </FhText>
    </ContentWrapper>
  </ContentPanel>

  <ContentPanel>
    <ContentWrapper class="success-sign-content-block">
      <FhText tag="h2" variant="display-md">
        {{ T('Does anyone else have to sign?') }}
      </FhText>

      <div class="buttons-container">
        <CopyButton v-if="!props.isDockDevice" size="md" is-signed-form />

        <FhButton
          class="wide-button"
          data-test-id="fh-button-sign-another-waiver"
          @click="handleSignAnotherClick"
        >
          {{ T('Sign another waiver') }}
        </FhButton>
      </div>
    </ContentWrapper>
  </ContentPanel>
</template>

<script setup>
import { FhButton, FhIcon, FhText } from '@fareharbor-com/beacon-vue';
import { check } from '@fareharbor-com/beacon-vue/icons';
import { computed } from 'vue';
import ContentPanel from '../shared/ContentPanel.vue';
import ContentWrapper from '../shared/ContentWrapper.vue';
import IconContainer from '../shared/IconContainer.vue';
import CopyButton from '../shared/copy-button/CopyButton.vue';
import { getIsTrackingEnabled, track } from '../../scripts/mixpanel';

const props = defineProps({
  email: {
    type: String,
    default: '',
  },
  isDockDevice: {
    type: Boolean,
    default: false,
  },
});

const computedConfirmationEmailText = computed(() =>
  interpolate(T('We have sent a confirmation email to %(email)s'), {
    email: props.email,
  }),
);

const TRACKING_TIMEOUT_MS = 1000;

const handleSignAnotherClick = () => {
  let hasNavigated = false;

  const openNewWaiver = () => {
    if (hasNavigated) return;
    hasNavigated = true;

    const searchParams = new URLSearchParams(window.location.search);

    if (props.isDockDevice) {
      const params = new Proxy(searchParams, {
        get: (innerParams, prop) => innerParams.get(prop),
      });

      const { source } = params;

      searchParams.set('source', source);
    } else {
      searchParams.set('source', 'sign-another');
    }

    window.location.href = `${window.location.origin}${window.location.pathname}?${searchParams.toString()}`;
  };

  if (getIsTrackingEnabled()) {
    track('click-fh-waiver-form-sign-another', null, openNewWaiver);
    // Fallback: some browsers (e.g. Safari with tracking protection) block
    // Mixpanel's network request, preventing the callback from ever firing.
    setTimeout(openNewWaiver, TRACKING_TIMEOUT_MS);
  } else {
    openNewWaiver();
  }
};
</script>

<style scoped>
.content-block {
  gap: var(--fh-size-md);
}

.success-sign-content-block {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.buttons-container {
  display: flex;
  align-items: center;
  gap: var(--fh-size-md);
}

@media only screen and (max-width: 679px) {
  .success-sign-content-block,
  .buttons-container {
    gap: var(--fh-space-2xl);
    flex-direction: column;
    width: 100%;
  }

  .wide-button {
    width: 100%;
  }
}
</style>
